import { useState, useEffect } from "react";
import axios from "axios";
import { Newspaper, RefreshCw, ExternalLink, TrendingUp, Plane, Building2, MapPin, Shield, Clock, Globe } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/layouts/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  market: TrendingUp,
  airline: Plane,
  hotel: Building2,
  destination: MapPin,
  policy: Shield,
};

const categoryColors = {
  market: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-100", badge: "bg-blue-100 text-blue-700" },
  airline: { bg: "bg-sky-50", text: "text-sky-600", border: "border-sky-100", badge: "bg-sky-100 text-sky-700" },
  hotel: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-100", badge: "bg-purple-100 text-purple-700" },
  destination: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-100", badge: "bg-emerald-100 text-emerald-700" },
  policy: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-100", badge: "bg-amber-100 text-amber-700" },
};

const TravelNewsPage = () => {
  const { token, user } = useAuth();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const dashType = (user?.role === 'admin' || user?.role === 'employee') ? 'admin' : 'agent';

  useEffect(() => { fetchNews(); }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/travel-news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout type={dashType}>
      <div className="space-y-6" data-testid="travel-news-page">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Newspaper className="w-5 h-5 text-emerald-600" />
              </div>
              AI Travel News
            </h1>
            <p className="text-sm text-gray-500 mt-1 ml-[52px]">AI-curated travel industry insights and latest headlines</p>
          </div>
          <button
            onClick={fetchNews}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors text-sm font-medium"
            data-testid="refresh-all-news"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Updated timestamp */}
        {news?.updated_at && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Clock className="w-3.5 h-3.5" />
            Last updated: {(() => { const d = new Date(news.updated_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}
            <span className="mx-1">|</span>
            <Globe className="w-3.5 h-3.5" />
            Sources: Skift, BTN, TTG Asia, HospitalityNet
          </div>
        )}

        {loading && !news ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-36 bg-white rounded-xl animate-pulse border border-gray-100" />
            ))}
          </div>
        ) : !news || (!news.ai_insights?.length && !news.latest_headlines?.length) ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-16 text-center">
              <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-lg font-medium">No travel news available right now</p>
              <p className="text-gray-400 text-sm mt-1">Check back later for the latest industry updates</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* AI Insights Section */}
            {news.ai_insights?.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-[10px] text-white bg-emerald-500 px-2 py-0.5 rounded-full font-medium">AI</span>
                  Key Insights
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="insights-grid">
                  {news.ai_insights.map((insight, i) => {
                    const cat = insight.category || "market";
                    const Icon = categoryIcons[cat] || TrendingUp;
                    const colors = categoryColors[cat] || categoryColors.market;
                    const hasLink = !!insight.link;
                    return (
                      <a
                        key={i}
                        href={insight.link || "#"}
                        target={hasLink ? "_blank" : undefined}
                        rel={hasLink ? "noreferrer" : undefined}
                        onClick={hasLink ? undefined : (e) => e.preventDefault()}
                        className={`group block rounded-xl border ${colors.border} ${colors.bg} p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${hasLink ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`news-insight-${i}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/80 border ${colors.border}`}>
                            <Icon className={`w-5 h-5 ${colors.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <Badge className={`${colors.badge} text-[10px] px-2 py-0 capitalize`}>{cat}</Badge>
                              {hasLink && <ExternalLink className="w-3 h-3 text-gray-300 group-hover:text-gray-500" />}
                            </div>
                            <h3 className="text-sm font-bold text-gray-900 leading-snug mb-2">{insight.headline}</h3>
                            <p className="text-xs text-gray-600 leading-relaxed">{insight.summary}</p>
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Latest Headlines Section */}
            {news.latest_headlines?.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gray-500" />
                  Latest Headlines
                </h2>
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardContent className="p-0">
                    <div className="divide-y divide-gray-50" data-testid="headlines-full-list">
                      {news.latest_headlines.map((h, i) => (
                        <a
                          key={i}
                          href={h.link}
                          target="_blank"
                          rel="noreferrer"
                          className="group flex items-center gap-4 px-5 py-4 hover:bg-emerald-50/30 transition-colors"
                          data-testid={`news-headline-${i}`}
                        >
                          <span className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 font-medium group-hover:text-emerald-700 leading-snug">{h.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{h.source}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-200 group-hover:text-emerald-500 flex-shrink-0 transition-colors" />
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TravelNewsPage;
